import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import { z } from "zod";

export type AssistantConversationSummaryJSON =
  z.infer<typeof AssistantConversationSummarySchema.AssistantConversationSummaryJSONZ>;
export type AssistantConversationSummaryParams =
  z.infer<typeof AssistantConversationSummarySchema.AssistantConversationSummaryZ>;

/**
 * A conversation as a list entry.
 *
 * Carries no answer text and no citation content. A list is rendered before anything has been
 * reauthorized, so the only text the server puts here is what the user typed themselves.
 */
export class AssistantConversationSummarySchema {
  public static readonly AssistantConversationSummaryJSONZ = z.object({
    conversationId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    lastMessageAt: z.iso.datetime({ offset: true }),
    turnCount: z.number(),
    opening: z.string(),
  });

  public static readonly AssistantConversationSummaryZ = z.object({
    conversationId: z.string(),
    startedAt: z.custom<AHDate>(),
    lastMessageAt: z.custom<AHDate>(),
    turnCount: z.number(),
    opening: z.string(),
  });

  public static validate(values: unknown): AssistantConversationSummaryParams {
    return parseOrThrow(AssistantConversationSummarySchema.AssistantConversationSummaryZ, values, {
      logError: true,
      schemaName: "AssistantConversationSummarySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantConversationSummaryParams {
    try {
      AssistantConversationSummarySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(
    json: AssistantConversationSummaryJSON
  ): AssistantConversationSummaryParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      AssistantConversationSummarySchema.AssistantConversationSummaryJSONZ,
      json,
      {
        logError: true,
        schemaName: "AssistantConversationSummarySchema",
        operation: "deserialize",
        payloadSample: json,
      }
    );
    return {
      conversationId: parsed.conversationId,
      startedAt: new AHDate(parsed.startedAt),
      lastMessageAt: new AHDate(parsed.lastMessageAt),
      turnCount: parsed.turnCount,
      opening: parsed.opening,
    };
  }
}

export default class AssistantConversationSummary {
  public static create(values: AssistantConversationSummaryParams): AssistantConversationSummary {
    const parsed = AssistantConversationSummarySchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantConversationSummary parameters.");
    }
    return new AssistantConversationSummary(parsed);
  }

  public static fromJSON(json: AssistantConversationSummaryJSON): AssistantConversationSummary {
    if (isNullOrUndefined(json)) return json;
    return new AssistantConversationSummary(
      AssistantConversationSummarySchema.deserialize(json)
    );
  }

  public readonly conversationId: string;
  public readonly startedAt: AHDate;
  public readonly lastMessageAt: AHDate;
  public readonly turnCount: number;
  public readonly opening: string;

  public constructor(params: AssistantConversationSummaryParams) {
    this.conversationId = params.conversationId;
    this.startedAt = params.startedAt;
    this.lastMessageAt = params.lastMessageAt;
    this.turnCount = params.turnCount;
    this.opening = params.opening;
  }
}
